import {Dialog, DialogBody, DialogDismiss, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useTranslation} from '@/translations/i18n'
import {StyledDialogHeader} from './style'
import {Text} from '@/components/ui/text/Text'
import {Button} from '@/components/ui/button/Button'
import {ChooseScaleFormSchema} from '../../formSchemas'
import {Controller, ControllerFieldState, ControllerRenderProps, UseFormReturn} from 'react-hook-form'
import dayjs from 'dayjs'
import {EvaluationScaleExtended, ScaleType} from '../../types'
import {scaleTypeToLabel} from '../../utils'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {InputText} from '@/components/commons/input-text/InputText'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {ComboboxItem, ComboboxProvider, SelectProvider, useComboboxStore} from '@ariakit/react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useState} from 'react'

export const ChooseScaleDialog = ({
    temporaryEvaluations,
    form,
    onSubmit
}: {
    temporaryEvaluations: EvaluationScaleExtended[]
    form: UseFormReturn<ChooseScaleFormSchema>
    onSubmit: () => void
}) => {
    const {t} = useTranslation()
    const handleSubmit = form.handleSubmit(onSubmit)

    const isTotalScoreShown =
        form.watch('type') &&
        form.watch('type') != ScaleType.enum.barthel_index &&
        form.watch('type') != ScaleType.enum.lawton_iadl

    return (
        <Dialog width="600px" overflowY="unset">
            <StyledDialogHeader>
                <Text render="h1" size="textMd" weight="500">
                    {t('projects:x_evaluation_scale', {count: temporaryEvaluations.length + 1})}
                </Text>
            </StyledDialogHeader>
            <DialogBody>
                <Controller
                    control={form.control}
                    name="evaluationDate"
                    render={({field, fieldState}) => (
                        <InputDate
                            label={t('projects:evaluation_date')}
                            placeholder="GG/MM/AAAA"
                            errorMessage={fieldState.error?.message}
                            disableAfterDate={dayjs().toDate()}
                            value={field.value ? dayjs(field.value).toDate() : undefined}
                            onChange={field.onChange}
                        />
                    )}
                />
                <Controller
                    control={form.control}
                    name="type"
                    render={({field, fieldState}) => (
                        <ScaleTypeSelect
                            form={form}
                            field={field}
                            fieldState={fieldState}
                            temporaryEvaluations={temporaryEvaluations}
                        />
                    )}
                />
                {isTotalScoreShown && (
                    <Controller
                        control={form.control}
                        name="totalScore"
                        render={({field, fieldState}) => (
                            <InputText
                                label={t('commons:total_score')}
                                type="number"
                                value={field.value?.toString()}
                                errorMessage={fieldState.error?.message}
                                onChange={event => {
                                    const value = event.target.value
                                    if (value == '') {
                                        return field.onChange(value)
                                    }
                                    if (isNaN(Number(value))) {
                                        return
                                    }
                                    field.onChange(Number(value))
                                }}
                            />
                        )}
                    />
                )}
            </DialogBody>
            <DialogFooter>
                <DialogDismiss onClick={() => form.reset()} render={<Button variant="secondary" />}>
                    {t('commons:close')}
                </DialogDismiss>
                <Button variant="primaryNeutral" onClick={handleSubmit}>
                    {t('projects:evaluation')}
                </Button>
            </DialogFooter>
        </Dialog>
    )
}

const ScaleTypeSelect = ({
    form,
    field,
    fieldState,
    temporaryEvaluations
}: {
    form: UseFormReturn<ChooseScaleFormSchema>
    field: ControllerRenderProps<ChooseScaleFormSchema, 'type'>
    fieldState: ControllerFieldState
    temporaryEvaluations: EvaluationScaleExtended[]
}) => {
    const {t} = useTranslation()
    const comboboxStore = useComboboxStore()
    const [search, setSearch] = useState('')

    return (
        <Flexbox direction="column" gap={1.5}>
            <Label>{t('commons:type')}</Label>
            <ComboboxProvider store={comboboxStore} setValue={setSearch} resetValueOnHide>
                <SelectProvider
                    defaultValue=""
                    value={field.value}
                    setValue={value => {
                        form.setValue('totalScore', null)
                        field.onChange(value)
                    }}
                >
                    <SelectTrigger
                        fullWidth
                        placeholder={t('commons:select')}
                        displayValue={value => t(scaleTypeToLabel[value as ScaleType])}
                    />
                    <SelectComboboxPopover>
                        <SelectComboboxInput autoFocus />
                        <SelectComboboxList>
                            {ScaleType.options
                                .filter(scaleType =>
                                    t(scaleTypeToLabel[scaleType]).toLowerCase().includes(search.toLowerCase())
                                )
                                .sort((a, b) => t(scaleTypeToLabel[a]).localeCompare(t(scaleTypeToLabel[b])))
                                .map(scaleType => (
                                    <SelectItem
                                        disabled={
                                            scaleType == form.watch('type') ||
                                            temporaryEvaluations.some(evaluation => evaluation.type == scaleType)
                                        }
                                        render={<ComboboxItem />}
                                        key={scaleType}
                                        value={scaleType}
                                    >
                                        {t(scaleTypeToLabel[scaleType])}
                                    </SelectItem>
                                ))}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </SelectProvider>
            </ComboboxProvider>
            {fieldState.error?.message && <InputHelpText error={t('errors:required')} />}
        </Flexbox>
    )
}
